import { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents, Circle } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { OpenStreetMapProvider } from "leaflet-geosearch";
import { MapPinIcon } from "@heroicons/react/20/solid";

const markerIcon = new L.Icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});


const LocationPicker = ({ onSave, onClose }) => {
  const [position, setPosition] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [nomeSede, setNomeSede] = useState("");
  const [raggio, setRaggio] = useState("");
  const [errors, setErrors] = useState({ nomeSede: "", raggio: "", posizione: "" });
  const [zoom, setZoom] = useState(15);
  const provider = new OpenStreetMapProvider();

  const handleSearch = async () => {
    if (!searchQuery) return;

    try {
      const results = await provider.search({ query: searchQuery });
      if (results.length > 0) {
        const { x, y } = results[0];
        setPosition([y, x]);
        setErrors((prev) => ({ ...prev, posizione: "" }));
      }
    } catch (error) {
      console.error("Errore nella ricerca dell'indirizzo:", error);
    }
  };
  
  function DraggableMarker() {
    const eventHandlers = {
      dragend(e) {
        const { lat, lng } = e.target.getLatLng();
        setPosition([lat, lng]);
      },
    };

    return position ? (
      <Marker position={position} icon={markerIcon} draggable eventHandlers={eventHandlers} />
    ) : null;
  }

  function MapUpdater() {
    const map = useMap();
  
    useEffect(() => {
      if (position) {
        map.setView(position, map.getZoom());
      }
    }, [position]);
  
    return null;
  }

  function LocationEvents() {
    const map = useMap();
  
    useMapEvents({
      click(e) {
        setPosition([e.latlng.lat, e.latlng.lng]);
        setErrors((prev) => ({ ...prev, posizione: "" }));
      },
      zoomend() {
        setZoom(map.getZoom()); 
      },
    });
  
    return null;
  }

  const handleSave = () => {
    let formErrors = { nomeSede: "", raggio: "", posizione: "" };
    let isValid = true;

    if (!nomeSede.trim()) {
      formErrors.nomeSede = "Inserire il nome";
      isValid = false;
    }

    if (!raggio.trim()) {
      formErrors.raggio = "Inserire un valore per il raggio";
      isValid = false;
    } else if (!/^[0-9]+$/.test(raggio)) {
      formErrors.raggio = "Inserire un numero intero";
      isValid = false;
    }

    if (!position) {
      formErrors.posizione = "Selezionare una posizione sulla mappa";
      isValid = false;
    }

    setErrors(formErrors);
    if (!isValid) return;

    const payload = {
      name: nomeSede,
      lat: position[0],
      lng: position[1],
      radius: parseInt(raggio, 10),
    };
    
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center" style={{ zIndex: 200 }}>
      <div className="bg-white p-4 rounded-lg shadow-lg w-1/2 h-[90vh] min-h-[90vh] flex flex-col">
        <h2 className="text-lg font-bold mb-2 text-gray-600">Aggiungi Sede</h2>

        {/* Nome sede */}
        <div className="mb-3">
          <label className="block text-sm font-semibold text-gray-600 mb-1">Nome Sede</label>
          <input
            type="text"
            className={`border p-2 w-full rounded ${errors.nomeSede ? 'border-red-500' : ''}`}
            value={nomeSede}
            onChange={(e) => {
              setNomeSede(e.target.value);
              setErrors((prev) => ({ ...prev, nomeSede: "" }));
            }}
            placeholder="Inserisci il nome della sede"
          />
          {errors.nomeSede && <p className="text-red-500 text-xs mt-1">{errors.nomeSede}</p>}
        </div>

        {/* Campo di ricerca indirizzo */}
        <label className="block text-sm font-semibold text-gray-600 mb-1">Indirizzo</label>
        <div className="flex mb-2">
          <input
            type="text"
            placeholder="Inserisci indirizzo..."
            className="border p-2 flex-1 rounded-l"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button
            onClick={handleSearch}
            className="flex items-center px-3 py-1 text-sm font-semibold text-white shadow-sm justify-center bg-indigo-600 hover:bg-indigo-400 rounded-r"
          >
            Cerca
          </button>
        </div>
        <div className="font-semibold">Inserisci l'indirizzo e il comune nel campo di ricerca.</div>
        <div className="pb-2 font-semibold">Per ottimizzare il posizionamento, seleziona e trascina il pin <MapPinIcon className="inline-block h-5 w-5 text-indigo-600" /> sulla mappa.</div>

        {/* Mappa */}
        <div className="flex-grow">
          <MapContainer center={[41.9028, 12.4964]} zoom={zoom} className="h-full w-full" >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MapUpdater />
            <LocationEvents />
            <DraggableMarker />
            {position && raggio && (
              <Circle center={position} radius={parseInt(raggio, 10)} color="blue" />
            )}
          </MapContainer>
        </div>
        {errors.posizione && <p className="text-red-500 text-xs mt-1">{errors.posizione}</p>}

        {/* Raggio consentito */}
        <div className="mt-2">
          <label className="block text-sm font-semibold text-gray-600 mb-1">Raggio (metri)</label>
          <input
            type="number"
            className={`border p-2 w-full rounded ${errors.raggio ? 'border-red-500' : ''}`}
            value={raggio}
            onChange={(e) => {
              if (/^\d*$/.test(e.target.value)) {
                setRaggio(e.target.value);
                setErrors((prev) => ({ ...prev, raggio: "" }));
              }
            }}
            placeholder="Es: 100"
          />
          <p className="text-xs text-gray-600 py-2 text-justify">
            Si consiglia di impostare un raggio di almeno <span className="underline text-red-500">100 metri</span>, poiché la precisione del GPS può variare. Un raggio troppo ristretto potrebbe impedire al dipendente di timbrare se la sua posizione viene rilevata al di fuori del limite, anche se si trova fisicamente sul posto.
          </p>

          {errors.raggio && <p className="text-red-500 text-xs mt-1">{errors.raggio}</p>}
        </div>

        {/* Pulsanti */}
        <div className="mt-4 flex justify-between flex-shrink-0">
          <button onClick={onClose} className="flex items-center rounded-md px-3 py-1 text-sm font-semibold text-white shadow-sm justify-center bg-gray-400 hover:bg-gray-300">Annulla</button>
          <button
            onClick={handleSave}
            className="flex items-center rounded-md px-3 py-1 text-sm font-semibold text-white shadow-sm justify-center bg-indigo-600 hover:bg-indigo-400"
          >
            Salva Sede
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationPicker;
